/**
 * This file should have common helper functions
 */

import { ErrorObject } from '@thoughtspot/blink-context';
import { getDurationObject } from '@thoughtspot/date-format-util';
import { translate } from '@thoughtspot/i18n';
import {
    formatBusinessNumber,
    formatDataValue,
} from '@thoughtspot/number-format-util';
import {
    OptionsGroup,
    SelectOption,
} from '@thoughtspot/radiant-react/widgets/select/base-select';
import {
    getJSessionId,
    getUserGuid,
    isSpotIQV2Enabled,
} from '@thoughtspot/session-service';
import _, { cloneDeep, includes, isNil, omitBy } from 'lodash';
import omitDeep from 'omit-deep-lodash';
import {
    A3AnalysisTypeEnum,
    A3Column,
    AnalysisAlgorithmTypeEnum,
    AnalysisClassEnum,
    ChangeAnalysisSummary,
    ChartViz,
    Cluster,
    ClusterOrSingleTile,
    ColumnType,
    FeedbackAnswerOption,
    FeedbackType,
    FrequencyGranularity,
    GeneralSettings,
    InsightAnalysisDetails,
    InsightData,
    OriginalAnalysisConfig,
    SingleTile,
} from '/@services/generated/graphql-types';
import {
    DEFAULT_SELECTED_FORECASTED_DATAPOINTS,
    DEFAULT_SELECTED_VENDOR,
    getDefaultInsightCountSettings,
} from '../../spotiq-box/spotiq-box.util';
import {
    AnalysisResultExpiry,
    columnDataType,
    SingleSelection,
    TranslationKeys,
} from '../spotiq-constants';

export function getLastUpdateAt(timeInMS: number) {
    const updatedTime = timeInMS * 1000;
    const duration = getDurationObject(Date.now() - updatedTime);
    return translate('jobRuns.hours.ago', {
        hours: duration.toFormat('h'),
    });
}
/**
 * this function can be used when needed to remove the __typename property from the graphql object
 * @param obj - any object
 * @returns object without __typename
 */
export function removeTypeNames(obj: Record<string, any>) {
    return omitDeep(cloneDeep(obj), '__typename');
}

/**
 * Remove any undefined or null property from an object
 * @param obj - any object
 * @returns object (object having non null values)
 */
export function removeEmpty(obj: any) {
    return omitBy(obj, isNil);
}

/**
 * takes the timestamp and calculates the number of days, hours and mins to expire
 * @param {number} timeRemaining
 * @returns {AnalysisResultExpiry}
 */

export function getTimeToExpire(timeRemaining: number): AnalysisResultExpiry {
    if (timeRemaining > 0) {
        const duration = getDurationObject(timeRemaining);
        const formattedRemainingTime = duration.toFormat('d,h,m').split(',');
        const remainingDays = Number(formattedRemainingTime[0]);
        const remainingHours = Number(formattedRemainingTime[1]);
        const remainingMins = Number(formattedRemainingTime[2]);
        return {
            remainingDays,
            remainingHours,
            remainingMins,
        };
    }
    return {
        remainingDays: 0,
        remainingHours: 0,
        remainingMins: 0,
    };
}

export function getFormattedNumOfRows(rows: number) {
    if (rows <= 0) {
        return rows;
    }
    return formatBusinessNumber(rows);
}

export function getHumanTime(durationInMs: number): string {
    const duration = getDurationObject(durationInMs);
    if (duration.days > 0) {
        return translate('DAYS_COUNT', {
            1: duration.as('days').toFixed(2),
        });
    }
    if (duration.hours > 0) {
        return translate('HOURS_COUNT', {
            numHours: duration.as('hours').toFixed(2),
        });
    }
    if (duration.minutes > 0) {
        return translate('MINUTES_COUNT', {
            numMinutes: duration.as('minutes').toFixed(2),
        });
    }
    return translate('SECONDS_COUNT', {
        numSeconds: duration.as('seconds').toFixed(2),
    });
}

export const getColumnData = (
    a3Columns: A3Column[],
    selectedTokensGuids: string[],
): columnDataType[] => {
    if (!a3Columns) {
        return [];
    }
    return a3Columns.map((a3Column: A3Column) => {
        const selectedTokenIndex = selectedTokensGuids.findIndex(
            tokenGuid => tokenGuid === a3Column.logicalColumn.guid,
        );
        return {
            column: a3Column.logicalColumn.displayName,
            columnId: a3Column.logicalColumn.guid,
            table: a3Column.ownerTable.displayName,
            isSuggested: a3Column.a3Suggested,
            isSelected: selectedTokenIndex !== -1,
            columnType: (a3Column.a3ColumnType as unknown) as ColumnType,
        };
    });
};

export const getGeneralSettingsForCustomAnalysis = (
    generalSettings: GeneralSettings,
) => [
    {
        title: translate(TranslationKeys.CURRENT_DATA_ONLY),
        isSelected: generalSettings.isAnalysisRestrictedToCurrentResultSet,
    },
    {
        title: translate(TranslationKeys.EXCLUDE_NULL_FROM_THIS_ANALYSIS),
        isSelected: generalSettings.isNullExcluded,
    },
    {
        title: translate(
            TranslationKeys.EXCLUDE_ZERO_MEASURE_FROM_THIS_ANALYSIS,
        ),
        isSelected: generalSettings.isZeroMeasureExclude,
    },
    {
        title: translate(
            TranslationKeys.AUTOTUNE_DATE_BOUNDARY_FROM_THIS_ANALYSIS,
        ),
        isSelected: generalSettings.isDateBoundaryAutoTuned,
    },
];

export const getFeedbackQuestion = (
    feedbackType: FeedbackType,
    feedbackQuestionParameters: string[],
) => {
    let feedbackQuestion: string;
    switch (feedbackType) {
        case FeedbackType.MeasureColumnUninteresting:
        case FeedbackType.AttributeColumnUninteresting:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_COLUMN_UNINTERESTING,
                {
                    column_name: feedbackQuestionParameters[0],
                },
            );
            break;

        case FeedbackType.MeasureFilterCombinationObvious:
        case FeedbackType.FilterColumnUninteresting:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_FILTER_COLUMN_UNINTERESTING,
            );
            break;

        case FeedbackType.TrendBetterDateAttribute:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_TREND_BETTER_DATE_ATTRIBUTE,
                {
                    date_attribute: feedbackQuestionParameters[0],
                },
            );
            break;

        case FeedbackType.CorrelationObvious:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_CORRELATION_OBVIOUS,
                {
                    measure1: feedbackQuestionParameters[0],
                    measure2: feedbackQuestionParameters[1],
                },
            );
            break;

        case FeedbackType.AllAnomaliesObvious:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_ALL_ANOMALIES_OBVIOUS,
            );
            break;

        case FeedbackType.TrendObvious:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_TREND_EXPECTED,
            );
            break;
        case FeedbackType.AnomalousPointUninteresting:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_ANOMALOUS_POINT_UNINTERESTING,
                {
                    anomalous_point: feedbackQuestionParameters[0],
                },
            );
            break;
        case FeedbackType.DiffValueUninteresting:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_DIFF_VALUES_UNINTERESTING,
                {
                    diff_value: feedbackQuestionParameters[0],
                },
            );
            break;
        case FeedbackType.DiffExpected:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_DIFFS_EXPECTED,
                {
                    diff_value: feedbackQuestionParameters[0],
                },
            );
            break;
        case FeedbackType.AllChangesExpected:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_ALL_CHANGES_EXPECTED,
                {
                    attribute: feedbackQuestionParameters[0],
                },
            );
            break;
        case FeedbackType.DataSourceUninteresting:
            feedbackQuestion = translate(
                TranslationKeys.FEEDBACK_QUESTION_DATA_SOURCE_UNINTERESTING,
                {
                    data_source: feedbackQuestionParameters[0],
                },
            );
            break;
        default:
    }
    return feedbackQuestion;
};

export const getMultiSelectedOptions = (
    selectedOptions: Set<string>,
    id: string,
): Set<string> => {
    const newSelectedOptions = new Set(selectedOptions);
    if (selectedOptions.has(id)) {
        newSelectedOptions.delete(id);
    } else {
        newSelectedOptions.add(id);
    }
    return newSelectedOptions;
};

export const getFeedbackOptionName = (name: string) => {
    const namesList = name.split('&');
    if (namesList.length === 1) {
        return namesList[0];
    }
    return translate(TranslationKeys.FEEDBACK_COLUMN_AND_FILTER_VALUE, {
        columnName: namesList[0],
        filterValue: namesList[1],
    });
};

export const addLabelToRadioButtons = (
    feedbackAnswerOptions: FeedbackAnswerOption[],
): SingleSelection[] => {
    return feedbackAnswerOptions.map(
        (feedbackAnswerOption: FeedbackAnswerOption, index: number) => {
            return {
                ...feedbackAnswerOption,
                label:
                    index === 1
                        ? translate(TranslationKeys.NO)
                        : translate(TranslationKeys.YES),
            };
        },
    );
};

/**
 * return options for frequency dropdown
 */
export const getFrequencyOptionsGroups = (): OptionsGroup[] => {
    const optionGroup: SelectOption[] = [
        {
            id: FrequencyGranularity.Hourly,
            label: translate('timeBucketLabels.h'),
            data: FrequencyGranularity.Hourly,
        },
        {
            id: FrequencyGranularity.Daily,
            label: translate('timeBucketLabels.d'),
            data: FrequencyGranularity.Daily,
        },
        {
            id: FrequencyGranularity.Weekly,
            label: translate('timeBucketLabels.w'),
            data: FrequencyGranularity.Weekly,
        },
        {
            id: FrequencyGranularity.Monthly,
            label: translate('timeBucketLabels.M'),
            data: FrequencyGranularity.Monthly,
        },
    ];
    return [
        {
            options: optionGroup,
        },
    ];
};

/**
 * this scenario should not come ideally
 * @returns OriginalAnalysisConfig
 */
export const getFallbackOriginalAnalysisConfig = (): OriginalAnalysisConfig => {
    const originalAnalysisConfig: OriginalAnalysisConfig = {
        selectedTokensGuids: [],
        analysisType: A3AnalysisTypeEnum.Visualization,
        selectedAnalysisClasses: !isSpotIQV2Enabled()
            ? [
                  AnalysisClassEnum.OutlierDetection,
                  AnalysisClassEnum.TrendAnalysis,
                  AnalysisClassEnum.CrossCorrelation,
              ]
            : [],
        selectedAlgorithmTypes: [
            AnalysisAlgorithmTypeEnum.MadMedian,
            AnalysisAlgorithmTypeEnum.LinearRegression,
        ],
        generalSettings: {
            isZeroMeasureExclude: false,
            isDateBoundaryAutoTuned: false,
            isAnalysisRestrictedToCurrentResultSet: true,
            isNullExcluded: false,
        },
        maxInsightsOpts: {
            maxTrends: 3,
            maxMeasures: getDefaultInsightCountSettings().maxMeasures,
            maxAnomalies: 20,
            maxLrAnomalies: 5,
            maxShesdAnomalies: 3,
            maxDrillAttributes: getDefaultInsightCountSettings()
                .maxDrillAttributes,
            maxCrossCorrelationInsights: 3,
            maxAnomalyExplanationInsights: 20,
        },
        parameterValues: {
            minRows: 5,
            multiplier: 2,
            pValueThreshold: 0.05,
            minRelativeDifference: 5,
            corrCoeff: 0.7,
            maxCorrCoeff: 0.98,
            maxLag: 50,
            maxDiffElements: 10,
            maxFraction: 0.5,
            minAbsChangeRatio: 0.1,
            minChangeRatio: 0.1,
            forecastedDatapoints: DEFAULT_SELECTED_FORECASTED_DATAPOINTS,
            vendor: DEFAULT_SELECTED_VENDOR,
        },
        isChangeAnalysis: false,
    };
    return originalAnalysisConfig;
};

/**
 * Get Analysis Type from Cluster or Single Tile
 * @param obj - ClusterOrSingleTile
 * @returns string
 */
export const getAnalysisTypeFromClusterOrSingleTile = (
    analysisResult: ClusterOrSingleTile,
) => {
    // eslint-disable-next-line no-underscore-dangle
    if (analysisResult?.__typename === 'Cluster') {
        return (analysisResult as Cluster).tiles[0]?.insightAnalysisDetails
            ?.analysisClass;
    }
    return (analysisResult as SingleTile)?.insightAnalysisDetails
        ?.analysisClass;
};

export const getSpotIQMixPanelActionProps = (params?: {
    [key: string]: string | number | boolean;
}) => {
    return {
        sessionId: getJSessionId(),
        userId: getUserGuid(),
        ...(params !== undefined && { ...params }),
    };
};

export const getAnalysisDetails = (
    analysisInfo: any[],
    ids: string[],
): any[] => {
    return analysisInfo.filter(anaysisData => includes(ids, anaysisData.id));
};

export const getUserTrackingInfo = (
    analysisDetails: any,
    selectedAnalysisCount: number,
) => {
    return {
        resultId: analysisDetails.id,
        resultStatus: analysisDetails.status.text,
        expiryStatus: analysisDetails.expiresIn.text,
        numberOfResults: selectedAnalysisCount,
        targetClass: '.bk-checkbox-label',
    };
};

export const getPageNumbr = (
    offset: number,
    batchSize: number,
    action: 'next' | 'prev',
) => {
    const pageNumbers = { currentPage: 0, nextPage: 1 };
    if (offset === 0) {
        pageNumbers.currentPage = 1;
        pageNumbers.nextPage =
            action === 'next' ? pageNumbers.currentPage + 1 : 1;
        return pageNumbers;
    }
    pageNumbers.currentPage = offset / batchSize;
    pageNumbers.nextPage =
        action === 'next'
            ? pageNumbers.currentPage + 1
            : pageNumbers.currentPage - 1;
    return pageNumbers;
};

export const getChangeSummaryTemplate = ({
    firstAggregate,
    secondAggregate,
    measureName,
    percentChange,
    firstPoint,
    secondPoint,
}: ChangeAnalysisSummary) => {
    let changeSummaryTemplate = TranslationKeys.ZERO_CHANGE_DESCRIPTION;
    const change = firstAggregate - secondAggregate;
    if (change > 0) {
        changeSummaryTemplate = TranslationKeys.POSITIVE_CHANGE_DESCRIPTION;
    } else if (change < 0) {
        changeSummaryTemplate = TranslationKeys.NEGATIVE_CHANGE_DESCRIPTION;
    }
    const computedChangeSummaryTemplate = translate(changeSummaryTemplate, {
        measureName,
        firstAggregate: formatDataValue(firstAggregate, {
            noShorten: false,
        }),
        percentChange: formatDataValue(percentChange, {
            noShorten: false,
        }),
        secondAggregate: formatDataValue(secondAggregate, {
            noShorten: false,
        }),
        firstPoint,
        secondPoint,
    });
    return computedChangeSummaryTemplate;
};

export enum RoleAttribute {
    button = 'button',
    option = 'option',
}

export function getChartVizWithInsight(
    chartViz: ChartViz,
    insightData: InsightData,
    enabled: boolean,
): ChartViz {
    if (!enabled || !chartViz || !insightData) {
        return chartViz;
    }
    const chartVizClone = _.cloneDeep(chartViz);
    chartVizClone.vizProp.chartProperties.showLegend = false;
    chartVizClone.vizProp.chartProperties.gridLines.xGridlineEnabled = true;
    chartVizClone.vizProp.chartProperties.allLabels = true;
    chartVizClone.vizProp.chartProperties.showStackedLabels = true;
    chartVizClone.cortexData = {
        forecasts: insightData,
        cacheToken: {},
    };
    return chartVizClone;
}

export const getIndivisualAnalysisDetails = (
    insightAnalysisDetails: InsightAnalysisDetails,
) => {
    const {
        mean,
        stdDev,
        meanMultiplier,
        alpha,
        minAbsChangeRatio,
        minChangeRatio,
        pValue,
        relativeDiff,
        pValueThresHold,
    } = insightAnalysisDetails;

    switch (insightAnalysisDetails.analysisClass) {
        case 'Standard Deviation Mean':
            return [
                `${translate(
                    TranslationKeys.INSIGHT_ANALYSIS_MEAN,
                )}: ${mean.toFixed(2)}`,
                `${translate(
                    TranslationKeys.ANALYSIS_STDDEV,
                )}: ${stdDev.toFixed(2)}`,
                `${translate(
                    TranslationKeys.ANALYSIS_MULTIPLIER,
                )}: ${meanMultiplier}`,
            ];
        case 'Median Absolute Deviation':
            return [
                `${translate(
                    TranslationKeys.INSIGHT_ANALYSIS_MEAN,
                )}: ${mean.toFixed(2)}`,
                `${translate(
                    TranslationKeys.ANALYSIS_STDDEV,
                )}: ${stdDev.toFixed(2)}`,
                `${translate(
                    TranslationKeys.ANALYSIS_MULTIPLIER,
                )}: ${meanMultiplier}`,
            ];
        case 'Seasonal Hybrid ESD':
            return [
                `${translate(TranslationKeys.ANALYSIS_ALPHA)}: ${alpha.toFixed(
                    2,
                )}`,
                `${translate(
                    TranslationKeys.ANALYSIS_MULTIPLIER,
                )}: ${meanMultiplier}`,
            ];
        case 'Absolute Difference Majority':
            return [
                `${translate(
                    TranslationKeys.ANALYSIS_MINABSCHNGRATIO,
                )}: ${minAbsChangeRatio.toFixed(2)}`,
                `${translate(
                    TranslationKeys.ANALYSIS_MINICHNGRATIO,
                )}: ${minChangeRatio.toFixed(2)}`,
            ];
        case 'Linear Regression':
            return [
                `${translate(
                    TranslationKeys.ANALYSIS_PVALTHRSHLD,
                )}: ${pValueThresHold.toFixed(2)}`,
                `${translate(
                    TranslationKeys.ANALYSIS_MULTIPLIER,
                )}: ${meanMultiplier}`,
            ];

        case 'Trend Analysis':
            return [
                `${translate(TranslationKeys.ANALYSIS_PVAL)}: ${pValue.toFixed(
                    2,
                )}`,
                `${translate(
                    TranslationKeys.ANALYSIS_RELDIFF,
                )}: ${relativeDiff.toFixed(2)}`,
            ];
        case 'Cross Correlation':
            return [];
        case 'Anomaly Explanation':
            return [];
        default:
            return [];
    }
};

export function hasInsightError(errors: ErrorObject[]) {
    return errors.some(_error => _error.queryName.includes('GetInsightData'));
}

export function getInsightErrorDesc(errors: ErrorObject[]) {
    let errorDesc = '';
    let errorTitle = '';
    switch (errors[0]?.code) {
        case '10076':
            errorTitle = translate('error.spotiq.analysis.stale.title');
            errorDesc = translate('error.spotiq.analysis.stale.desc');
            break;
        case '10077':
            errorTitle = translate(
                'error.spotiq.analysis.insight.missing.title',
            );
            errorDesc = translate('error.spotiq.analysis.insight.missing.desc');
            break;
        default:
            // 10038 internal server error
            errorTitle = translate('error.loading.default');
            errorDesc = translate('error.validation.default');
    }
    return { errorDesc, errorTitle };
}
